import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { WaterDropIcon, TargetIcon, TelescopeIcon, PersonIcon, AwardIcon, GlobeIcon } from '../components/Icons';
import './About.css';

/* ---------- Small utilities ---------- */
const useCountUpOnView = (end = 0, duration = 1200) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const hasRun = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasRun.current) {
            hasRun.current = true;
            const start = performance.now();
            const animate = (t) => {
              const p = Math.min((t - start) / duration, 1);
              setValue(Math.floor(p * end));
              if (p < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.25 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);

  return { ref, value };
};

const About = () => {
  const years = useCountUpOnView(15);
  const projects = useCountUpOnView(500);
  const clients = useCountUpOnView(1000);
  const support = useCountUpOnView(24);

  return (
    <div className="about-page">
      <Helmet>
        <title>About Pure Drop Technicals - Water Solution Experts</title>
        <meta
          name="description"
          content="Learn about Pure Drop Technicals - South Africa's leading water purification and business setup experts. 15+ years of experience in water solutions."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="about-hero section">
        <div className="container">
          <div className="about-hero-content">
            <div className="about-text">
              <h1>About Pure Drop Technicals</h1>
              <p className="lead">
                Transforming water challenges into business opportunities across South Africa for over 15 years.
              </p>

              <div className="hero-cta">
                <Link to="/services" className="btn btn-light">Explore Services</Link>
                <Link to="/request" className="btn btn-outline">Free Consultation</Link>
              </div>
            </div>

            <div className="about-visual">
              <div className="water-drop" aria-hidden="true">
                <WaterDropIcon size={64} className="hero-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* “About Us – Serving Communities…” */}
      <section className="about-serving section">
        <div className="container">
          <div className="serving-layout">
            <div className="serving-title">
              <h2>About Us — Serving Communities by Making a Difference</h2>
            </div>
            <div className="serving-body">
      <div className="about-brief">
  <div className="about-copy">
    <p>
      Safe drinking water is still scarce in too many communities, so we build turnkey systems that
      help locals launch sustainable water businesses and serve their neighbours.
    </p>
    <p>
      Every plant we install is engineered for tough African water sources, opening jobs while
      ensuring families receive reliable, great-tasting water every day.
    </p>
  </div>
</div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="company-story section">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>Our Story</h2>
              <p>
                We started with a simple idea: unlock clean water for communities while giving entrepreneurs a profitable path into the water industry.
              </p>
              <p>
                Today we deliver turnkey shops, container plants, and premium water lines across South Africa, supporting 500+ owners who now serve thousands of customers every day.
              </p>
              <p>
                From compact storefronts to industrial RO systems, every build is engineered for reliability, compliance, and hands-on support so each client can scale with confidence.
              </p>
            </div>

            <div className="story-stats" aria-label="company stats">
              <div className="stat-item" ref={years.ref}>
                <div className="stat-number">{years.value}+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-item" ref={projects.ref}>
                <div className="stat-number">{projects.value}+</div>
                <div className="stat-label">Successful Projects</div>
              </div>
              <div className="stat-item" ref={clients.ref}>
                <div className="stat-number">{clients.value}+</div>
                <div className="stat-label">Happy Clients</div>
              </div>
              <div className="stat-item" ref={support.ref}>
                <div className="stat-number">{support.value}/7</div>
                <div className="stat-label">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="mission-vision section">
        <div className="container">
          <div className="mvv-grid">
            <div className="mvv-item">
              <div className="mvv-icon">
                <TargetIcon size={48} className="mvv-icon-svg" />
              </div>
              <h3>Our Mission</h3>
              <p>
                To provide innovative, sustainable water purification solutions that empower entrepreneurs and communities across South Africa to access clean, safe
                drinking water while building profitable businesses.
              </p>
            </div>

            <div className="mvv-item">
              <div className="mvv-icon">
                <TelescopeIcon size={48} className="mvv-icon-svg" />
              </div>
              <h3>Our Vision</h3>
              <p>
                To be South Africa&apos;s most trusted partner for water business solutions, leading the transformation of how communities access and benefit from
                clean water technology.
              </p>
            </div>

            <div className="mvv-item">
              <div className="mvv-icon">
                <PersonIcon size={48} className="mvv-icon-svg" />
              </div>
              <h3>Our Values</h3>
              <ul>
                <li><strong>Trust:</strong> Building lasting relationships through transparency and reliability</li>
                <li><strong>Quality:</strong> Delivering solutions that exceed industry standards</li>
                <li><strong>Innovation:</strong> Embracing cutting-edge technology and sustainable practices</li>
                <li><strong>Sustainability:</strong> Protecting our environment for future generations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Expert Team</h2>
            <p>Experienced professionals dedicated to your water business success</p>
          </div>

          <div className="team-grid">
            <div className="team-member">
              <h4>Technical Engineering Team</h4>
              <p>Certified treatment specialists who design, install, and commission every plant with precision.</p>
            </div>

            <div className="team-member">
              <h4>Business Development</h4>
              <p>Consultants who turn your idea into a launch plan, from site layout to opening specials.</p>
            </div>

            <div className="team-member">
              <h4>Quality Assurance</h4>
              <p>Lab specialists who certify every drop meets South African quality standards.</p>
            </div>

            <div className="team-member">
              <h4>Support Services</h4>
              <p>24/7 technicians ready with maintenance, troubleshooting, and on-site training.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="certifications section">
        <div className="container">
          <div className="cert-content">
            <div className="cert-text">
              <h2>Certifications & Compliance</h2>
              <p>Pure Drop Technicals maintains the highest industry standards and regulatory compliance:</p>

              <ul className="cert-list">
                <li>✓ SANS 241 Water Quality Standards Compliance</li>
                <li>✓ ISO 9001 Quality Management System</li>
                <li>✓ Department of Water and Sanitation Approved</li>
                <li>✓ South African Bureau of Standards Certified</li>
                <li>✓ Environmental Impact Assessment Certified</li>
                <li>✓ Professional Engineering Council Registration</li>
              </ul>
            </div>

            <div className="cert-badges">
              <div className="cert-badge">
                <div className="badge-icon">
                  <AwardIcon size={48} className="badge-icon-svg" />
                </div>
                <div className="badge-text">SANS 241<br />Certified</div>
              </div>
              <div className="cert-badge">
                <div className="badge-icon" aria-hidden="true">✅</div>
                <div className="badge-text">ISO 9001<br />Quality</div>
              </div>
              <div className="cert-badge">
                <div className="badge-icon">
                  <GlobeIcon size={48} className="badge-icon-svg" />
                </div>
                <div className="badge-text">Eco<br />Certified</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta section">
        <div className="container">
          <div className="cta-box">
            <h2>Ready to Start Your Water Business Journey?</h2>
            <p>Join hundreds of successful entrepreneurs who trust Pure Drop Technicals for their water business solutions.</p>
            <div className="cta-buttons">
              <Link to="/request" className="btn btn-primary">Get Free Consultation</Link>
              <Link to="/services" className="btn btn-outline">Explore Our Services</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
